import { HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { ConnectingAirportsOutlined } from '@mui/icons-material';
import { Coordinate } from 'ol/coordinate';
import WKT from 'ol/format/WKT';
import { LineString, MultiLineString } from 'ol/geom';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useUpdateMap } from '../components/worldMap/api/useWorldMapController';
import { GeopostMap } from '../models/classes/GeopostMap';
import { SeismicCalculator } from '../models/classes/SeismicCalculator';
import { LineType } from '../models/enums/LineType';
import { SurveyType } from '../models/enums/SurveyType';
import { ILineString } from '../models/interfaces/ILineString';
import { ISurveyMetadata } from '../models/interfaces/ISurveyMetadata';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { useMapStore } from '../stores/useMapStore';
import { useSeismicStore } from '../stores/useSeismicStore';
import { getTrace } from '../utils/calculateScreenValuesUtils';
import { getTraceValue } from '../utils/getLabelsUtils';
import { mouseToMap } from '../utils/seismicToMapCalculate';
import { useSessionStore } from 'session/useSessionStore';

interface paramsUpdateSeismic {
    point: Coordinate | null,
    lineString: string,
    lineLeft: number[] | Coordinate[] | null,
    lineRight: number[] | Coordinate[] | null,
    srid: number,
}

// Função de throttling
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function throttle(func: (...args: any[]) => void, limit: number) {
    let lastFunc: ReturnType<typeof setTimeout>;
    let lastRan: number;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function(...args: any[]) {
        if (!lastRan) {
            func(...args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(function() {
                if ((Date.now() - lastRan) >= limit) {
                    func(...args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
}

export function useMapSignalR(dataGeometryLine: ILineString | null) {
    const connectionStartedRef = useRef<boolean>(false);

    const { map } = useMapStore(state => ({
        map: state.map
    }));

    const { scaleX, restrict, calculator, surveyMetadata, isInverted } = useSeismicStore(state => ({
        scaleX: state.scale.x,
        restrict: state.restrict,
        calculator: state.calculator,
        surveyMetadata: state.surveyMetadata,
        isInverted: state.isInverted
    }));

    const jwt = useSessionStore(state => state.jwtToken);

    const lineType = useLine3DNavigationStore(state => state.lineType);

    const signalRHost = useSessionStore(state => state.tenantConfiguration?.endpoints.signalR);

    const signalRConnection = useMemo(() => {
        return !!signalRHost ?
            new HubConnectionBuilder()
                .withUrl(signalRHost + '/ConnectionHub',{
                    skipNegotiation: true,
                    transport: HttpTransportType.WebSockets,
                })
                .configureLogging(LogLevel.Debug)
                .build()
            :
            null;
    }, [signalRHost]);

    useEffect(() => {
        if (!!signalRConnection) {
            signalRConnection.start().then(() => {
                connectionStartedRef.current = true;
            });
        }
    }, [signalRConnection]);

    const [paramsMap, setParamsMap] = useState<paramsUpdateSeismic | null>(null);

    useEffect(() => {
        if (connectionStartedRef.current && !!paramsMap && !!signalRConnection) {
            signalRConnection.invoke('Notify', jwt, 'updateSeismic', paramsMap);
        }
    }, [paramsMap]);

    const [geometrySeismic, setGeometrySeismic] = useState<LineString[] | null>(null);
    const [updateMap, setUpdateMap] = useState<boolean>(false);
    const srid = useSessionStore(state => state.tenantConfiguration?.miniMapSrid ?? 4326);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    /*const throttledPointerMove = useRef(throttle((map: GeopostMap, dataGeometryLine: ILineString, geometrySeismic: LineString, calculator: SeismicCalculator, surveyMetadata: ISurveyMetadata, event: any, isInvertedLine: boolean) => {
        const coordinates = mouseToMap(calculator, surveyMetadata, event.coordinate, dataGeometryLine, isInverted, scaleX, geometrySeismic);

        setUpdateMap(true);
        setParamsMap({
            point: coordinates,
            lineString: dataGeometryLine.Line,
            //lineLeft: fracLeft,
            //lineRight: fracRight,
            lineLeft: null,
            lineRight: null,
        });
    }, 300)).current;
    */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const throttledPointerMove = throttle((map: GeopostMap, dataGeometryLine: ILineString, geometrySeismic: LineString[], calculator: SeismicCalculator, surveyMetadata: ISurveyMetadata, event: any, isInvertedLine: boolean) => {
        const coordinates = mouseToMap(calculator, surveyMetadata, event.coordinate, dataGeometryLine, isInverted, scaleX, geometrySeismic, surveyMetadata.VolumeDebugInfo);

        setUpdateMap(true);
        setParamsMap({
            point: coordinates,
            lineString: dataGeometryLine.Line,
            srid,
            //lineLeft: fracLeft,
            //lineRight: fracRight,
            lineLeft: null,
            lineRight: null,
        });
    }, 300);

    // Envia para a tela de mapa através do signalR
    // info da linha
    useEffect(() => {
        if (dataGeometryLine && dataGeometryLine.Line) {
            let format = new WKT();

            const featureLine = format.readFeature(dataGeometryLine.Line, {
                dataProjection: `EPSG:3395`,
                featureProjection: `EPSG:3395`,
            });

            const geom = featureLine.getGeometry();
            if (geom instanceof MultiLineString) {
                setGeometrySeismic(geom.getLineStrings());
            }
            else {
                setGeometrySeismic([geom as LineString]);
            }
        }
    }, [dataGeometryLine]);

    // parte das coordenadas do espaço da tela visivel na linha e do mouse
    useEffect(() => {
        if (!map || !geometrySeismic || !dataGeometryLine || !restrict || !calculator || !surveyMetadata) {
            return;
        }

        if (!restrict.SeismicDisplayRealTimePositionOnMap) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const moveHandler = (event: any) => throttledPointerMove(map, dataGeometryLine, geometrySeismic, calculator, surveyMetadata, event, isInverted);

        map.on('pointermove', moveHandler);

        return () => {
            map.un('pointermove', moveHandler);
        };
    }, [map, geometrySeismic, dataGeometryLine, restrict, calculator, surveyMetadata, throttledPointerMove, isInverted]);
}
